import { Injectable } from '@angular/core';
import { Actions, createEffect, Effect, ofType } from '@ngrx/effects';
import { PositionsService } from '../../../units/services/positions.service';
import { DataGrabber } from '../../../units/services/data-grabber';
import { catchError, exhaustMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { interval, of } from 'rxjs';
import { getPositions, updatePositions } from '../actions/positions.actions';
import { getPositionsFailure, getPositionsSuccess, updatePositionsSuccess } from '../actions/positions-api.actions';
import { Store } from '@ngrx/store';
import * as reducer from '../reducers';
import { mergePositionsWithUnits, updateUnits } from '../actions/unit-all-data.actions';
import { cacheLifeTime } from '../../../shared/config';
import * as UnitSharedActions from '../actions/units-shared.actions';
import { Position } from '../../../units/models/position';
import * as moment from 'moment-timezone';
import { PositionStatus } from '../../../units/models/position-status';

@Injectable()
export class PositionsEffects {

  getPositions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getPositions.type),
      switchMap((action: any) => this.positionService.getPositions(action.unitIds)
        .pipe(
          exhaustMap(positions => {
            positions?.forEach(p => this.setPositionStatus(p));
            return of(getPositionsSuccess({positions: positions}));
          }),
          catchError(error => of(getPositionsFailure({error})))
        )
      )
    )
  );

  getPositionsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getPositionsSuccess.type),
      withLatestFrom(this.store$.select(reducer.getUnits)),
      switchMap(([action, units]) => {
        const allUnits = this.dataGrabber.createAllDataList(units, action['positions']);
        return of(mergePositionsWithUnits({unitAllData: allUnits}));
      })
    )
  );

  updatePositions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updatePositions.type),
      withLatestFrom(
        this.store$.select(reducer.getPositions),
        this.store$.select(reducer.getUnitAllData),
      ),
       tap(([action, positions, units]) => {
        positions?.forEach(p => this.setPositionStatus(p));
        const allUnits = this.dataGrabber.updateAllDataList(units, action['positions']);
        (<any>action['positions'])?.forEach(x => {
          const index = (<any>positions)?.findIndex(y => y.unitId === x.unitId);
          if (index > -1) {
            (<any>positions)[index] = x;
          } else {
            (<any>positions)?.push(x);
          }
        });
        this.store$.dispatch(updatePositionsSuccess({positions: <any>positions}));
        this.store$.dispatch(updateUnits({unitAllData: allUnits}));
      })
    ),
    { dispatch: false }
  );

  @Effect()
  clear = interval(cacheLifeTime).pipe(
    switchMap((action) => of(UnitSharedActions.clearData()))
  );

  constructor(
    private actions$: Actions,
    private store$: Store<any>,
    private positionService: PositionsService,
    private dataGrabber: DataGrabber
  ) { }

  private setPositionStatus(position: Position) {
    if (!position) { return; }

    if ((position.ignition || position.speed > 2) && moment(position.date).isAfter(moment().subtract(5, 'minute'))) {
      position.status = PositionStatus.MOVING;
    } else if (!position.ignition && moment(position.date).isAfter(moment().subtract(65, 'minute'))) {
      position.status = PositionStatus.PARKING;
    } else {
      position.status = PositionStatus.INACTIVE;
    }
  }
}
